import { useTranslation } from 'react-i18next';
import StatusBadge from './StatusBadge';

export interface Reminder {
  id: string;
  title: string;
  description?: string;
  due_date: string;
  source: 'auto' | 'manual';
  status: string;
  source_signal_id?: string;
}

interface ReminderCardProps {
  reminder: Reminder;
  onSnooze: (id: string) => void;
  onComplete: (id: string) => void;
  onDismiss: (id: string) => void;
  onReschedule: (id: string) => void;
}

function dueDateStyle(dueDate: string): { color: string; label: string } {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.floor(
    (due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays < 0) return { color: 'text-red-600', label: 'overdue' };
  if (diffDays === 0) return { color: 'text-orange-600', label: 'today' };
  return { color: 'text-gray-500', label: 'upcoming' };
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function ReminderCard({
  reminder,
  onSnooze,
  onComplete,
  onDismiss,
  onReschedule,
}: ReminderCardProps) {
  const { t } = useTranslation();
  const dueStyle = dueDateStyle(reminder.due_date);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
      {/* Title + status */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="text-sm font-semibold text-gray-900 leading-snug flex-1">
          {reminder.title}
        </h4>
        <StatusBadge status={reminder.status} type="reminder" />
      </div>

      {/* Description */}
      {reminder.description && (
        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
          {reminder.description}
        </p>
      )}

      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-3 text-xs mb-3">
        {/* Due date */}
        <span className={`font-medium ${dueStyle.color}`}>
          {t(`common.${dueStyle.label}`)} &middot; {formatDate(reminder.due_date)}
        </span>

        {/* Source badge */}
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-gray-600 font-medium">
          {reminder.source === 'auto'
            ? t('reminders.autoCreated')
            : t('reminders.manuallyCreated')}
        </span>

        {/* Link to source signal */}
        {reminder.source_signal_id && (
          <span className="text-primary-600 underline cursor-pointer">
            {'📡'} Signal
          </span>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2">
        <button
          onClick={() => onComplete(reminder.id)}
          className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors"
        >
          {'✓'} {t('reminders.complete')}
        </button>
        <button
          onClick={() => onSnooze(reminder.id)}
          className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors"
        >
          {t('common.snooze')}
        </button>
        <button
          onClick={() => onReschedule(reminder.id)}
          className="inline-flex items-center gap-1 rounded-md bg-yellow-50 px-2.5 py-1 text-xs font-medium text-yellow-700 hover:bg-yellow-100 transition-colors"
        >
          {t('reminders.reschedule')}
        </button>
        <button
          onClick={() => onDismiss(reminder.id)}
          className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors ltr:ml-auto rtl:mr-auto"
        >
          {t('common.dismiss')}
        </button>
      </div>
    </div>
  );
}
